import { Fragment } from 'react'
import { isPartialOn } from '../lib/dates.js'
import { OffBadge, OffPartialTile } from './OffMarker.jsx'

/**
 * The wall display's day view: a timetable, which is what the whiteboard it replaces
 * actually is. Names down the side, job slots across the top, crews as banded sections.
 *
 * It is a dedicated component rather than the board's grid because the two want different
 * things — the board's table is an editing surface with hover states, drag handles and
 * context menus, none of which mean anything on a screen nobody touches. Keeping them
 * separate is also what stopped the wall and the board fighting over about forty CSS
 * override rules.
 */
/**
 * Which people get a row under which crew: home membership first, then anyone borrowed in
 * for the day — otherwise a borrowed person's work is indexed to a crew whose section never
 * draws it. Exported because the pagination has to know how tall each crew will be BEFORE
 * it decides what goes on a page.
 */
export function crewRows(board, date) {
  const rowsFor = new Map(board.groups.map((g) => [g.id, [...g.staffIds]]))
  for (const a of board.assignments) {
    if (a.date !== date) continue
    const rows = rowsFor.get(a.renderGroupId)
    if (rows && !rows.includes(a.staffId)) rows.push(a.staffId)
  }
  return rowsFor
}

export default function WallDay({ board, date }) {
  const day = board.days.find((d) => d.date === date) || board.days[0]
  const slotCount = day?.slotCount || 3
  const slots = Array.from({ length: slotCount }, (_, i) => i + 1)

  const staffById = new Map(board.staff.map((s) => [s.id, s]))
  const groupsById = new Map(board.groups.map((g) => [g.id, g]))

  const rowsFor = crewRows(board, date)
  const cellIndex = new Map() // `${renderGroupId}:${staffId}:${slot}` → assignment
  const lentOut = new Map() // `${homeGroupId}:${staffId}:${slot}` → where they went

  // Away today. On a wall this matters more than anywhere else: the board is the only thing
  // a crew checks before heading out, and a blank row reads as "not scheduled yet".
  // The whole day takes the row out, as it always has. A window within it keeps the row and
  // says when — on a wall that matters twice over, because the jobs either side of a
  // dentist appointment are still the jobs the crew is looking up here.
  const offToday = new Map()
  const offPartToday = new Map()
  for (const t of board.timeOff || []) {
    if (!(t.from <= date && date <= t.to)) continue
    if (isPartialOn(t, date)) {
      if (!offPartToday.has(t.staffId)) offPartToday.set(t.staffId, t)
    } else {
      offToday.set(t.staffId, t)
    }
  }

  for (const a of board.assignments) {
    if (a.date !== date) continue

    cellIndex.set(`${a.renderGroupId}:${a.staffId}:${a.slot}`, a)

    // A borrow leaves a note in the home crew's section, per slot: someone can do their own
    // crew's work at Job 1 and be lent out at Job 2, and collapsing that to a whole-row
    // banner hides the job they are actually doing for you.
    if (a.borrowedFromGroupId) {
      lentOut.set(`${a.borrowedFromGroupId}:${a.staffId}:${a.slot}`, a)
    }
  }

  return (
    <table className="wall-table">
      <thead>
        <tr>
          <th className="wall-col-staff">Staff</th>
          {slots.map((s) => (
            <th key={s}>Job {s}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        {board.groups.map((group, gi) => {
          const rows = rowsFor.get(group.id) || []
          const style = group.color ? { '--team': group.color } : undefined

          return (
            <Fragment key={group.id || `unassigned-${gi}`}>
              <tr className="wall-crew" style={style}>
                <td colSpan={slotCount + 1}>
                  <span className="wall-crew-line">
                    <span className="wall-crew-name">{group.name}</span>
                    {group.coordinatorName && (
                      <span className="wall-crew-lead">{group.coordinatorName}</span>
                    )}
                  </span>
                </td>
              </tr>

              {rows.map((staffId) => {
                const staff = staffById.get(staffId)
                if (!staff) return null
                const visiting = board.mode === 'cat' && !group.staffIds.includes(staffId)
                const off = offToday.get(staffId)
                const partOff = off ? null : offPartToday.get(staffId)

                return (
                  <tr
                    key={staffId}
                    className={`wall-row ${visiting ? 'visiting' : ''} ${off ? 'off' : ''}`}
                    style={style}
                  >
                    <td className="wall-col-staff">
                      {staff.name}
                      {visiting && !off && <span className="wall-guest">guest</span>}
                    </td>
                    {off ? (
                      <td className="wall-off off-hatch" colSpan={slotCount}>
                        <OffBadge t={off} />
                      </td>
                    ) : slots.map((slot) => {
                      const entry = cellIndex.get(`${group.id}:${staffId}:${slot}`)
                      const away = !entry && lentOut.get(`${group.id}:${staffId}:${slot}`)
                      return (
                        <td key={slot}>
                          {/* In the first job cell, not on the name: a 22% column has no room for
                              a name and a window both, and it was the name that lost. Here it
                              sits with the day’s work, which is what it is about. */}
                          {slot === 1 && partOff && <OffPartialTile t={partOff} date={date} />}
                          {entry ? (
                            <span className="wall-job">{entry.label}</span>
                          ) : away ? (
                            <span className="wall-away">
                              {groupsById.get(away.catTeamId)?.name || 'another crew'}
                            </span>
                          ) : day?.holiday?.observed ? (
                            // Same reason the Off badge is here rather than a blank row: on a
                            // wall, an empty cell reads as "not scheduled yet". A booked cell
                            // still shows the job — a stat that somebody is working is the
                            // thing the crew most needs to see.
                            <span className="wall-stat">STAT</span>
                          ) : null}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}

              {/* Its own class, not `wall-row`. It is a shorter line than a name — the
                  pagination measures a staff row and multiplies it by a row count, and a
                  placeholder answering to the same selector meant the measurement sometimes
                  sampled this instead, understating every crew on the board. */}
              {rows.length === 0 && (
                <tr className="wall-none">
                  <td className="wall-empty" colSpan={slotCount + 1}>No one scheduled</td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}
